import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { STOCK, HERO_LEATHER } from '../experience/assets';

const fade = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8 },
};

const PROCESS = [
  'Material Sourcing', 'Leather Selection', 'Pattern & Cutting', 'Stitching',
  'Assembly', 'Finishing', 'Quality Control', 'Final Inspection',
];

const PRODUCTS = [
  'Premium Leather Jackets', 'Leather Garments', 'Leather Footwear',
  "Kids' Leather Footwear", 'Leather Bags & Accessories', 'Custom Leather Products',
];

const VALUES = ['Quality', 'Precision', 'Consistency', 'Reliability'];

const STRENGTH = [
  { value: '35+ Years', label: 'of leather manufacturing experience' },
  { value: '5,000+', label: 'leather garments produced monthly' },
  { value: '50+', label: 'skilled production professionals' },
  { value: 'Global', label: 'experience serving international markets & brands' },
];

function Eyebrow({ children }) {
  return <span className="font-accent text-[10px] text-[#8C5A35] block mb-5">{children}</span>;
}

export default function AboutUs() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main data-testid="about-page" className="relative bg-[#0A0A0A] text-[#F5F5F0] min-h-screen">
      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-12 py-5 bg-[#0A0A0A]/70 backdrop-blur-md border-b border-[#2A2A2A]">
        <Link to="/" className="font-display text-xl md:text-2xl tracking-wide text-[#F5F5F0]">
          PT <span className="italic text-[#B89B72]">Exports</span>
        </Link>
        <Link
          to="/"
          data-testid="about-back"
          data-hover
          className="group inline-flex items-center gap-2 border border-[#B89B72]/50 text-[#B89B72] px-5 py-2.5 font-accent text-[10px] hover:bg-[#8C5A35]/20 transition-colors duration-300"
        >
          <ArrowLeft className="w-3.5 h-3.5 transition-transform duration-300 group-hover:-translate-x-0.5" strokeWidth={1.4} />
          Back to Experience
        </Link>
      </header>

      {/* Hero */}
      <section className="relative pt-40 md:pt-52 pb-24 md:pb-32 px-6 md:px-16 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: `url(${STOCK.rawLeather})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/85 to-[#0A0A0A]" />
        <div className="relative max-w-5xl">
          <Eyebrow>About PTExports</Eyebrow>
          <motion.h1
            {...fade}
            className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.03] tracking-tight"
          >
            Crafting Leather.<br /><span className="italic text-[#B89B72]">Building Legacies.</span>
          </motion.h1>
          <motion.div {...fade} transition={{ duration: 0.8, delay: 0.15 }} className="mt-10 space-y-5 max-w-2xl">
            <p className="font-body font-light text-sm md:text-base text-[#A3A3A3] leading-relaxed">
              For over 35 years, PTExports has been dedicated to the art and precision of leather manufacturing.
              Founded with a commitment to quality craftsmanship and responsible manufacturing, we have grown into a
              trusted leather manufacturing partner for international brands — producing premium leather garments,
              footwear, and leather products from our manufacturing base in India.
            </p>
            <p className="font-body font-light text-sm md:text-base text-[#A3A3A3] leading-relaxed">
              What began as a manufacturing vision has evolved into a company built around craftsmanship,
              consistency, and long-term partnerships.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 35+ Years of Leather Expertise */}
      <section className="px-6 md:px-16 py-20 md:py-28 border-t border-[#2A2A2A]">
        <motion.div {...fade} className="max-w-3xl">
          <Eyebrow>35+ Years of Leather Expertise</Eyebrow>
          <h2 className="font-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight mb-6">
            Leather is more than a material.<br /><span className="italic text-[#B89B72]">It is a craft.</span>
          </h2>
          <div className="space-y-5 font-body font-light text-sm md:text-base text-[#A3A3A3] leading-relaxed">
            <p>
              A craft that demands experience, precision, and attention to detail. With more than three decades of
              industry experience, our teams understand every stage of the leather manufacturing process — from
              material sourcing and selection to cutting, stitching, finishing, quality control, and final production.
            </p>
            <p>
              Our experience allows us to work with different leather types, finishes, designs, and construction
              techniques while maintaining the quality standards expected by international customers.
            </p>
          </div>
        </motion.div>
      </section>

      {/* From Raw Material to Finished Product */}
      <section className="px-6 md:px-16 py-20 md:py-28 border-t border-[#2A2A2A] bg-[#0d0b09]">
        <motion.div {...fade} className="max-w-3xl mb-14">
          <Eyebrow>From Raw Material to Finished Product</Eyebrow>
          <h2 className="font-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight mb-6">
            Manufacturing built around <span className="italic text-[#B89B72]">complete process control.</span>
          </h2>
          <p className="font-body font-light text-sm md:text-base text-[#A3A3A3] leading-relaxed">
            Every stage is carefully monitored to ensure consistency in workmanship, finishing, fit, and quality. Our
            teams work closely across departments to transform designs and specifications into finished products ready
            for global markets.
          </p>
        </motion.div>

        <div className="flex flex-wrap items-center gap-3 md:gap-4">
          {PROCESS.map((step, i) => (
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="flex items-center gap-3 md:gap-4"
            >
              <div className="flex items-center gap-3 border border-[#2A2A2A] bg-[#111111]/60 px-4 md:px-5 py-3">
                <span className="font-accent text-[9px] text-[#8C5A35]">{String(i + 1).padStart(2, '0')}</span>
                <span className="font-body font-light text-xs md:text-sm text-[#F5F5F0]">{step}</span>
              </div>
              {i < PROCESS.length - 1 && <span className="text-[#8C5A35] font-light hidden sm:inline">&rarr;</span>}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Manufacturing for Global Standards */}
      <section className="px-6 md:px-16 py-20 md:py-28 border-t border-[#2A2A2A]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <motion.div {...fade}>
            <Eyebrow>Manufacturing for Global Standards</Eyebrow>
            <h2 className="font-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight mb-6">
              Products designed for <span className="italic text-[#B89B72]">demanding global markets.</span>
            </h2>
            <p className="font-body font-light text-sm md:text-base text-[#A3A3A3] leading-relaxed mb-6">
              Over the years, PTExports has worked with international customers and brands. Our manufacturing experience
              includes producing leather footwear for international brands such as
              <span className="text-[#F5F5F0]"> Geox</span> and <span className="text-[#F5F5F0]">Kickers</span>,
              alongside a wide range of leather garments and products.
            </p>
          </motion.div>

          <motion.ul {...fade} transition={{ duration: 0.8, delay: 0.1 }} className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#2A2A2A] border border-[#2A2A2A] self-start">
            {PRODUCTS.map((p) => (
              <li key={p} className="flex items-center gap-3 bg-[#0A0A0A] px-5 py-5">
                <Check className="w-4 h-4 text-[#B89B72] shrink-0" strokeWidth={1.5} />
                <span className="font-body font-light text-sm text-[#F5F5F0]">{p}</span>
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* Craftsmanship Meets Manufacturing Discipline */}
      <section className="relative px-6 md:px-16 py-24 md:py-32 border-t border-[#2A2A2A] overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: `url(${STOCK.darkTexture})` }} />
        <div className="absolute inset-0 bg-[#0A0A0A]/85" />
        <motion.div {...fade} className="relative max-w-3xl">
          <Eyebrow>Craftsmanship Meets Manufacturing Discipline</Eyebrow>
          <h2 className="font-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight mb-6">
            Where craftsmanship <span className="italic text-[#B89B72]">meets precision.</span>
          </h2>
          <p className="font-body font-light text-sm md:text-base text-[#A3A3A3] leading-relaxed">
            Our skilled production teams combine traditional leatherworking expertise with structured manufacturing
            processes to achieve consistent results at scale. From the precision of a single stitch to the final
            finishing of a complete garment, every detail contributes to the character and quality of the finished product.
          </p>
        </motion.div>
        <div className="relative flex flex-wrap gap-3 md:gap-4 mt-10">
          {VALUES.map((v, i) => (
            <motion.span
              key={v}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="font-accent text-[11px] text-[#B89B72] border border-[#B89B72]/40 px-6 py-3"
            >
              {v}
            </motion.span>
          ))}
        </div>
      </section>

      {/* Built Around Long-Term Partnerships */}
      <section className="px-6 md:px-16 py-20 md:py-28 border-t border-[#2A2A2A] bg-[#0d0b09]">
        <motion.div {...fade} className="max-w-3xl">
          <Eyebrow>Built Around Long-Term Partnerships</Eyebrow>
          <h2 className="font-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight mb-6">
            Relationships that <span className="italic text-[#B89B72]">last.</span>
          </h2>
          <div className="space-y-5 font-body font-light text-sm md:text-base text-[#A3A3A3] leading-relaxed">
            <p>
              We believe manufacturing is not simply about producing an order. It is about understanding a client&rsquo;s
              requirements, maintaining consistent quality, meeting production timelines, and building relationships
              that last.
            </p>
            <p>
              Our approach is built around collaboration — working closely with customers from product development and
              sampling through production and delivery. This partnership-driven approach has helped PTExports build
              lasting relationships within the international leather industry.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Our Manufacturing Strength */}
      <section className="px-6 md:px-16 py-20 md:py-28 border-t border-[#2A2A2A]">
        <motion.div {...fade} className="mb-14">
          <Eyebrow>Our Manufacturing Strength</Eyebrow>
        </motion.div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
          {STRENGTH.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="bg-[#0A0A0A] px-6 py-10 md:py-14"
            >
              <div className="font-display text-3xl md:text-5xl text-[#B89B72] font-light">{s.value}</div>
              <div className="font-body font-light text-[11px] md:text-xs text-[#A3A3A3] mt-3 leading-relaxed">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Our Commitment */}
      <section className="px-6 md:px-16 py-20 md:py-28 border-t border-[#2A2A2A] bg-[#0d0b09]">
        <motion.div {...fade} className="max-w-3xl">
          <Eyebrow>Our Commitment</Eyebrow>
          <p className="font-body font-light text-sm md:text-base text-[#A3A3A3] leading-relaxed mb-6">
            As the leather industry continues to evolve, PTExports remains committed to improving the way we manufacture.
            We continuously invest in our people, processes, quality systems, and manufacturing capabilities to meet the
            changing expectations of global customers.
          </p>
          <h2 className="font-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
            To create leather products that combine <span className="italic text-[#B89B72]">exceptional craftsmanship</span> with dependable manufacturing.
          </h2>
        </motion.div>
      </section>

      {/* The PTExports Promise */}
      <section className="relative px-6 md:px-16 py-28 md:py-40 border-t border-[#2A2A2A] text-center overflow-hidden">
        <div className="absolute inset-0 opacity-25 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_LEATHER})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]" />
        <motion.div {...fade} className="relative">
          <Eyebrow>The PTExports Promise</Eyebrow>
          <h2 className="font-display font-light text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
            Made with Experience.<br />Built with Precision.<br /><span className="italic text-[#B89B72]">Trusted Across Borders.</span>
          </h2>
          <p className="font-body font-light text-sm md:text-base text-[#A3A3A3] leading-relaxed max-w-2xl mx-auto mt-10">
            For more than three decades, PTExports has been turning leather into products that move from our factory to
            markets around the world. And we are only getting started.
          </p>
          <Link
            to="/"
            data-testid="about-back-bottom"
            data-hover
            className="group inline-flex items-center gap-3 border border-[#B89B72]/60 text-[#B89B72] px-8 py-4 mt-12 font-accent text-[10px] hover:bg-[#8C5A35]/20 transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-0.5" strokeWidth={1.2} />
            Back to Experience
          </Link>
        </motion.div>
      </section>

      <footer className="px-6 md:px-16 py-10 border-t border-[#2A2A2A]">
        <div className="font-accent text-[9px] text-[#525252]">
          &copy; {new Date().getFullYear()} Pakkar Tanveer Exports. 35+ years of craftsmanship.
        </div>
      </footer>
    </main>
  );
}
